// UserContext.js
import React, { createContext, useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import api from "./api";
import { WebSocketProvider } from './components/socket';
import TabNotifier from './components/TabNotifier';
import SubscribePanel from './components/SubscribePanel';
import { useTranslation } from './LanguageContext';
import { isAuthenticated } from './utils/authHelper';
const UserContext = createContext();

/**
 * `api/UserData/` never answered and the tokens are still in place, so the
 * session itself may be fine — the server, the network or CORS was not.
 *
 * There is no safe screen to send this visitor to. Falling through renders
 * `user.account_type` on `null` and throws, which blanks the page; bouncing
 * to the landing page is worse, because `isAuthenticated()` still says yes
 * there and sends them straight back here, forever. So the failure is stated
 * where it happened, with the one action that can help.
 */
function LoadFailed({ onRetry }) {
    const { t } = useTranslation();
    return (
        <div className="flex items-center justify-center min-h-screen p-6">
            <div className="text-center max-w-sm">
                <h1 className="text-lg font-bold text-slate-800 mb-2">{t('session.title')}</h1>
                <p className="text-sm text-slate-500 mb-5">{t('session.desc')}</p>
                <button type="button" onClick={onRetry} className="btn-primary px-6 py-2.5 text-sm">
                    {t('session.retry')}
                </button>
            </div>
        </div>
    );
}

function PaywallModal() {
    const { t } = useTranslation();
    const [code, setCode] = useState("");
    // The card form opens inside this dialog rather than as a second one on top
    // of it: nesting two full-screen scrims compounds them into a black slab,
    // and this modal is already the only thing on screen.
    const [paying, setPaying] = useState(false);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    const handlePromo = async (e) => {
        e.preventDefault();
        try {
            const res = await api.post('/api/check-promo/', { code });
            if (res.status === 200) {
                window.location.reload();
            }
        } catch (error) {
            alert(t('payments.promoInvalid'));
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
                {/* Header */}
                <div className="px-6 pt-8 pb-4 text-center bg-gradient-to-br from-primary-600 to-primary-700">
                    <div className="flex justify-center mb-6">
                        <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 flex items-center gap-2.5 shadow-lg">
                            <span className="relative flex h-2.5 w-2.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                            </span>
                            <span className="text-sm font-medium text-white">{t('payments.newMessagesAlert')}</span>
                        </div>
                    </div>
                    <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-black text-white mb-1">{t('payments.subscribeTitle')}</h2>
                    <p className="text-sm text-white/80">{t('payments.subscribeDesc')}</p>
                </div>

                {/* Body */}
                <div className="px-6 py-6 space-y-5 max-h-[70vh] overflow-y-auto custom-scrollbar">
                    {paying ? (
                        /* Paid here — reload once it lands, because the socket
                           layer and every gated page read the subscription at
                           mount rather than watching it. */
                        <SubscribePanel
                            target="platform"
                            onSubscribed={() => setTimeout(() => window.location.reload(), 1200)}
                            onCancel={() => setPaying(false)}
                        />
                    ) : (
                        <>
                            {/* Subscribe button */}
                            <button type="button" className="btn-primary w-full py-3 text-base font-bold"
                                onClick={() => setPaying(true)}>
                                {t('payments.payNowBtn')}
                            </button>

                            {/* Divider */}
                            <div className="flex items-center gap-3">
                                <div className="flex-1 h-px bg-slate-200"></div>
                                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{t('payments.promoTitle')}</span>
                                <div className="flex-1 h-px bg-slate-200"></div>
                            </div>

                            {/* Promo code form */}
                            <form onSubmit={handlePromo} className="space-y-3">
                                <input
                                    placeholder={t('payments.promoPlaceholder')}
                                    className="input"
                                    type="text"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    maxLength={511}
                                    required
                                />
                                <button type="submit" className="btn-success w-full">
                                    {t('payments.applyCodeBtn')}
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function AccountTypeModal({ onComplete }) {
    const { t } = useTranslation();
    const [selected, setSelected] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    const options = [
        {
            key: "creator",
            label: t('accountType.creatorTitle'),
            desc: t('accountType.creatorDesc'),
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
            ),
            gradient: "from-amber-400 to-orange-500",
            ring: "ring-amber-400",
            bg: "bg-amber-50",
            border: "border-amber-200",
            iconColor: "text-amber-500",
        },
        {
            key: "brand",
            label: t('accountType.brandTitle'),
            desc: t('accountType.brandDesc'),
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
            ),
            gradient: "from-violet-500 to-purple-600",
            ring: "ring-violet-400",
            bg: "bg-violet-50",
            border: "border-violet-200",
            iconColor: "text-violet-500",
        },
        {
            key: "user",
            label: t('accountType.userTitle'),
            desc: t('accountType.userDesc'),
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
            ),
            gradient: "from-sky-400 to-blue-500",
            ring: "ring-sky-400",
            bg: "bg-sky-50",
            border: "border-sky-200",
            iconColor: "text-sky-500",
        },
    ];

    const handleConfirm = async () => {
        if (!selected) return;
        setLoading(true);
        try {
            const res = await api.post('/api/change-account-type/', { account_type: selected });
            if (res.status === 200) {
                window.location.reload();
            }
        } catch (error) {
            setLoading(false);
            setShowConfirm(false);
        }
    };

    // Confirmation dialog
    if (showConfirm) {
        const opt = options.find(o => o.key === selected);
        return (
            <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4">
                <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden animate-fade-in">
                    <div className="px-6 pt-7 pb-5 text-center">
                        {/* Warning icon */}
                        <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-bold text-slate-800 mb-2">{t('accountType.confirmTitle')}</h3>
                        <p className="text-sm text-slate-500 mb-1">
                            {t('accountType.confirmSelected')}: <span className="font-semibold text-slate-700">{opt?.label}</span>
                        </p>
                        <p className="text-xs text-amber-600 font-medium bg-amber-50 rounded-lg px-3 py-2 mt-3">
                            ⚠️ {t('accountType.confirmWarning')}
                        </p>
                    </div>
                    <div className="px-6 pb-6 flex gap-3">
                        <button
                            onClick={() => setShowConfirm(false)}
                            disabled={loading}
                            className="btn-secondary flex-1 py-2.5 text-sm"
                        >
                            {t('accountType.confirmCancel')}
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={loading}
                            className="btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-2"
                        >
                            {loading && <span className="spinner-sm"></span>}
                            {t('accountType.confirmBtn')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Main selection modal
    return (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-fade-in">
                {/* Header */}
                <div className="px-6 pt-8 pb-5 text-center bg-gradient-to-br from-primary-600 to-primary-700">
                    <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-black text-white mb-1">{t('accountType.title')}</h2>
                    <p className="text-sm text-white/80">{t('accountType.subtitle')}</p>
                </div>

                {/* Options */}
                <div className="px-5 py-5 space-y-3">
                    {options.map((opt) => (
                        <button
                            key={opt.key}
                            onClick={() => setSelected(opt.key)}
                            className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-200 text-left
                                ${selected === opt.key
                                    ? `${opt.border} ${opt.bg} ring-2 ${opt.ring} ring-offset-1 scale-[1.02]`
                                    : "border-slate-100 bg-white hover:border-slate-200 hover:bg-slate-50"
                                }
                            `}
                        >
                            {/* Icon */}
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0
                                ${selected === opt.key
                                    ? `bg-gradient-to-br ${opt.gradient} text-white shadow-lg`
                                    : `${opt.bg} ${opt.iconColor}`
                                }
                            `}>
                                {opt.icon}
                            </div>
                            {/* Text */}
                            <div className="flex-1 min-w-0">
                                <p className={`font-bold text-sm ${selected === opt.key ? "text-slate-800" : "text-slate-700"}`}>
                                    {opt.label}
                                </p>
                                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{opt.desc}</p>
                            </div>
                            {/* Check */}
                            {selected === opt.key && (
                                <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${opt.gradient} flex items-center justify-center flex-shrink-0`}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                            )}
                        </button>
                    ))}
                </div>

                {/* Submit */}
                <div className="px-5 pb-6">
                    <button
                        onClick={() => { if (selected) setShowConfirm(true); }}
                        disabled={!selected}
                        className="btn-primary w-full py-3 text-base font-bold"
                    >
                        {t('accountType.submitBtn')}
                    </button>
                </div>
            </div>
        </div>
    );
}

const UserProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refreshUser = async () => {
        try {
            const response = await api.get("api/UserData/");
            if (response.data.number == null) {
                response.data.number = "";
            }
            let u = response.data;
            setUser(prevUser => ({
                ...u,
                last: prevUser?.last || [],
                active: prevUser?.active || null,
            }));
        } catch (error) {}
    };

    const load = useCallback(() => {
        setLoading(true);
        api.get("api/UserData/").then((response) => {
            if (response.data.number == null) {
                response.data.number = "";
            }
            let u = response.data;
            u["last"] = []
            u["active"] = null;
            setUser(u);
            setLoading(false);
        }
        ).catch((error) => {
            setLoading(false);
        });
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading) {
        return <div className="flex items-center justify-center h-screen"><div className="spinner" role="status">
        </div></div>;
    }

    // The request failed and there is no user. Navbar and every screen below
    // read this object, so rendering on throws — and a torn-down React tree
    // cannot be navigated out of afterwards, which is how a dead session
    // turned into a blank page.
    //
    // The tokens decide where to go, not the error: `api.js` clears them when
    // the session is genuinely dead, and only then can the landing page hold
    // us — `isAuthenticated()` there is the exact thing that would bounce us
    // back into this provider on a loop. Gone: leave, in-router, without
    // reloading the document, the way ProtectedRoute does. Still there: this
    // was the transport, so say so rather than spin.
    if (!user) {
        if (!isAuthenticated()) {
            return <Navigate to="/" replace />;
        }
        return <LoadFailed onRetry={load} />;
    }
    return (
        <UserContext.Provider value={{ user, setUser, refreshUser }}>
            <WebSocketProvider>
                {/* Renders nothing; mirrors the unread badge into the browser
                    tab, which is where the user can actually see it. */}
                <TabNotifier />
                <Navbar />
                {children}
                {user.account_type === "default" && <AccountTypeModal />}
                {user.account_type !== "default" && user.subscription.status !== "active" && <PaywallModal />}
            </WebSocketProvider>
        </UserContext.Provider>
    );
};
export { UserContext, UserProvider };
